import { memo, useState } from "react";
import { useTranslations } from "next-intl";
import {
  IconCheck,
  IconChevronRight,
  IconLoader2,
  IconSettingsBolt,
  IconX,
} from "@tabler/icons-react";
import { type AiMessage } from "@/providers/AiAssistantProvider";
import { ToolStepRow } from "./ToolStepRow";

/**
 * A collapsible block for a RUN of consecutive tool calls — the model can
 * chain a dozen+ tool steps in one turn (e.g. "add 15 tables") and a flat list
 * of those rows buries the actual conversation. Collapsed, it reads as a single
 * "N steps" toggle (ChatGPT-style); expanded, it shows every ToolStepRow.
 *
 * Open state: defaults to OPEN while any step is still running (so live
 * progress stays visible during the turn) and auto-collapses once the run
 * finishes — unless the user has manually toggled it, which wins from then on.
 */
export const ToolStepGroup = memo(function ToolStepGroup({
  tools,
  variant = "chat",
}: {
  tools: AiMessage[];
  variant?: "chat" | "builder";
}) {
  const t = useTranslations("aiAssistant");
  const builder = variant === "builder";
  const sz = builder ? 12 : 13;

  const running = tools.some((m) => (m.toolStatus ?? "running") === "running");
  const errors = tools.filter((m) => m.toolStatus === "error").length;

  // null = follow the default (open while running); a boolean = user override.
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? running;

  const headerIcon = running ? (
    <IconLoader2 size={sz} className="animate-spin motion-reduce:animate-none" />
  ) : errors > 0 ? (
    <IconX size={sz} />
  ) : (
    <IconCheck size={sz} />
  );

  const headerText = running
    ? t("toolGroupRunning", { count: tools.length })
    : t("toolGroupSteps", { count: tools.length });

  // Collapsed + running: keep the live step visible so the toggle isn't a
  // silent multi-minute spinner.
  const liveStep = !open && running ? tools[tools.length - 1] : null;

  return (
    <div className="flex flex-col items-start gap-1.5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setUserOpen(!open)}
        data-testid={builder ? "ai-builder-tool-group" : "ai-tool-group"}
        className={`inline-flex items-center gap-1.5 rounded-full border outline-quaternary px-2.5 py-1 text-xs font-tertiary transition-colors hover:surface-secondary cursor-pointer ${
          errors > 0 && !running ? "font-error" : ""
        }`}
      >
        {!builder && <IconSettingsBolt size={sz} className="shrink-0" />}
        {headerIcon}
        <span className="truncate">{headerText}</span>
        <IconChevronRight
          size={sz}
          className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {liveStep && <ToolStepRow message={liveStep} variant={variant} />}
      {open && (
        <div className="flex flex-col items-start gap-1.5 border-l outline-quaternary pl-2.5">
          {tools.map((m) => (
            <ToolStepRow key={m.id} message={m} variant={variant} />
          ))}
        </div>
      )}
    </div>
  );
});
